import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Store service similar a Zustand pero para Angular
 * Maneja el estado global y la persistencia
 */
export abstract class StoreService<T extends Record<string, any>> {
  protected stateSubject!: BehaviorSubject<T>;
  public state$!: Observable<T>;
  protected abstract getInitialState(): T;
  protected abstract getStorageKey(): string | undefined;

  private initialized = false;

  constructor() {
    // Inicializar con estado por defecto inmediatamente (síncrono, rápido)
    const initialState = this.getInitialState();
    this.stateSubject = new BehaviorSubject<T>(initialState);
    this.state$ = this.stateSubject.asObservable();
    
    // Cargar desde storage de forma asíncrona (no bloquea)
    this.initializeAsync();
  }

  /**
   * Inicializa el store de forma asíncrona
   */
  private initializeAsync(): void {
    // Usar requestIdleCallback con timeout más largo para no competir con renderizado inicial
    const runWhenIdle = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 100));
    
    runWhenIdle(() => {
      if (this.initialized) return;
      
      const storageKey = this.getStorageKey();
      
      // Intentar cargar el estado desde localStorage si hay storageKey
      const savedState = storageKey 
        ? this.loadFromStorage(storageKey) 
        : null;
      
      if (savedState) {
        this.stateSubject.next(savedState);
      }
      
      // Guardar automáticamente cuando el estado cambia (diferido aún más)
      if (storageKey) {
        // Diferir suscripción significativamente
        setTimeout(() => {
          this.state$.subscribe(state => {
            this.saveToStorage(storageKey, state);
          });
        }, 500);
      }
      
      this.initialized = true;
    });
  }

  /**
   * Obtiene el estado actual
   */
  getState(): T {
    return this.stateSubject.value;
  }

  /**
   * Obtiene un selector del estado
   */
  select<K>(selector: (state: T) => K): Observable<K> {
    return this.state$.pipe(
      map(selector),
      distinctUntilChanged()
    );
  }

  /**
   * Actualiza el estado
   */
  setState(partial: Partial<T> | ((state: T) => Partial<T>)): void {
    const currentState = this.stateSubject.value;
    const newState = typeof partial === 'function' 
      ? { ...currentState, ...partial(currentState) }
      : { ...currentState, ...partial };
    
    this.stateSubject.next(newState as T);
  }

  /**
   * Resetea el estado al estado inicial
   */
  reset(): void {
    this.stateSubject.next(this.getInitialState());
    const storageKey = this.getStorageKey();
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  }

  /**
   * Carga el estado desde localStorage
   */
  private loadFromStorage(storageKey: string): T | null {
    try {
      // Usar try-catch rápido sin bloquear
      const item = localStorage.getItem(storageKey);
      if (!item) return null;
      
      // Parse rápido - si falla, retornar null
      return JSON.parse(item) as T;
    } catch {
      // Silenciar errores para no bloquear
      return null;
    }
  }

  /**
   * Guarda el estado en localStorage (diferido para no bloquear)
   */
  private saveToStorage(storageKey: string, state: T): void {
    // Usar debounce para evitar escrituras excesivas
    if ((this as any).saveTimeout) {
      clearTimeout((this as any).saveTimeout);
    }
    
    (this as any).saveTimeout = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch {
        // Silenciar errores
      }
    }, 300);
  }
}

